




















































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { fontFaceList } from '../../views/wheelOfFortune/constant/options';
import { getFontFaceList } from '@/utils/getFontFaceList';
import { Message } from 'element-ui';
import { uploadFont, saveFont, getCompanyFontList } from '@/services/fontApi';
import { loadCustomFont } from '@/utils/fontLoader';

// 缓存公司字体，避免多次重复请求 cartsee.formFont.list
let companyFontsCache: any[] | null = null;
let companyFontsLoading: Promise<any> | null = null;

const FONT_LIST_UPDATED_EVENT = 'cartsee:font-list-updated';

@Component
class FontPicker extends Vue {
  /**
   * Vue2 默认 v-model：value + input。勿再用 @Model('change')，否则与父级 @change 冲突且 undefined 时 el-select 异常。
   */
  @Prop({ type: String, default: '' }) readonly value!: string;
  @Prop({ type: String, default: '' }) readonly placeholder!: string;

  get resolvedPlaceholder(): string {
    return this.placeholder || (this.$t('components.fontpicker.text_8bf7900962e9') as string);
  }
  @Prop({ type: Boolean, default: false }) readonly showAddButton!: boolean;

  localValue: string = '';
  dialogVisible: boolean = false;
  uploading: boolean = false;

  // 用于避免当前实例在 submitUpload 内主动刷新后，
  // 又被自身触发的全局事件重复刷新
  lastFontListRefreshAt: number = 0;

  /**
   * 格式化字体名称，对于包含空格或中文的字体名称，在CSS中用引号包裹
   */
  formatFontFamily(fontFamily: string): string {
    if (!fontFamily) return fontFamily;

    // 分割字体列表，分别处理每个字体
    const fonts = fontFamily.split(',').map(font => {
      const trimmedFont = font.trim();

      // 移除现有的引号，然后重新评估是否需要添加
      const unquotedFont = trimmedFont.replace(/^"|"$/g, '');

      // 检查是否包含空格或中文字符，如果是则加上引号
      if (/\s/.test(unquotedFont) || /[\u4e00-\u9fa5]/.test(unquotedFont)) {
        return `"${unquotedFont}"`;
      }
      return unquotedFont;
    });

    return fonts.join(', ');
  }

  onFontListUpdated = () => {
    const now = Date.now();
    // 500ms 内跳过重复刷新
    if (now - this.lastFontListRefreshAt < 500) return;
    this.lastFontListRefreshAt = now;

    companyFontsCache = null;
    this.loadCompanyFonts();
  };

  uploadForm: any = {
    name: '',
    file: null,
    fallback: 'CartxHelvetica'
  };

  get rules() {
    return {
      name: [{ required: true, message: this.$t('components.fontpicker.text_8bf78f9351655b57') as string, trigger: 'blur' }],
      fallback: [{ required: true, message: this.$t('components.fontpicker.text_8bf7900962e95907') as string, trigger: 'change' }]
    };
  }

  fontList = getFontFaceList(fontFaceList).map(font => ({
    ...font,
    value: font.value // 保持原始值不变，因为通用字体通常是预定义的安全字体名称
  }));

  /** 父级绑定 undefined/null（如 styleMap 尚未有 font-family）时统一成空串，保证 el-select 可选 */
  normalizeValue(v: any): string {
    if (v === undefined || v === null) return '';
    return String(v);
  }

  /** 将已保存的 font-family 映射到当前 fontList 中某一项的 value，否则 el-select 有值也不显示 label */
  resolveFontOptionValue(raw: any): string {
    const trimmed = this.normalizeValue(raw).trim();
    if (!trimmed) return '';
    const list = this.fontList || [];
    const exact = list.find((f: any) => f.value === trimmed);
    if (exact) return exact.value;
    const byName = list.find((f: any) => f.name === trimmed);
    if (byName) return byName.value;
    const firstToken = trimmed.split(',')[0].trim();
    const byFirst = list.find((f: any) => {
      const v0 = String(f.value).split(',')[0].trim();
      return v0 === firstToken || f.name === firstToken;
    });
    if (byFirst) return byFirst.value;
    return trimmed;
  }

  get selectRerenderKey() {
    return `fp-${this.fontList.length}-${this.brandFonts.length}`;
  }

  async mounted() {
    this.localValue = this.resolveFontOptionValue(this.value);
    await this.loadCompanyFonts();
    this.localValue = this.resolveFontOptionValue(this.value);
    this.$nextTick(() => this.$forceUpdate());

    window.addEventListener(FONT_LIST_UPDATED_EVENT, this.onFontListUpdated);
  }

  beforeDestroy() {
    window.removeEventListener(FONT_LIST_UPDATED_EVENT, this.onFontListUpdated);
  }

  async loadCompanyFonts() {
    try {
      // 已有缓存，直接使用
      if (companyFontsCache) {
        this.applyCompanyFonts(companyFontsCache);
        return;
      }

      // 如果正在加载中，复用同一个 Promise，避免并发多次请求
      if (!companyFontsLoading) {
        companyFontsLoading = getCompanyFontList()
          .then((response: any) => {
            if (response.data && response.data.code === 0) {
              companyFontsCache = response.data.data || [];
            } else {
              companyFontsCache = [];
            }
            return companyFontsCache;
          })
          .finally(() => {
            companyFontsLoading = null;
          });
      }

      const fonts = await companyFontsLoading;
      this.applyCompanyFonts(fonts || []);
    } catch (error) {
      console.error('Failed to load company fonts:', error);
    }
  }

  applyCompanyFonts(fonts: any[]) {
    // 获取通用字体 (原始硬编码字体) 每次从原始列表生成，避免被多次叠加
    const commonFonts = getFontFaceList(fontFaceList);

    // 处理从API获取的品牌字体
    const brandFontsFromApi: Array<{ name: string; value: string; original: boolean; brand: boolean }> = [];

    (fonts || []).forEach((font: any) => {
      const newFont = {
        name: font.fontName,
        value: `${this.quoteFontNameIfNeeded(font.fontName)}, ${font.defaultFont}`,
        original: false,
        brand: true, // 标记为品牌字体
      };
      brandFontsFromApi.push(newFont);

      // 预加载字体
      if (font.url && font.fontName) {
        loadCustomFont(font.url, font.fontName);
      }
    });

    // 重新组合字体列表: API获取的品牌字体 + 通用字体
    this.fontList = [...brandFontsFromApi, ...commonFonts];

    // 强制更新以确保UI正确显示
    this.$nextTick(() => {
      this.localValue = this.resolveFontOptionValue(this.value);
      this.$forceUpdate();
    });
  }

  /**
   * 根据需要给字体名称加引号（如果包含空格或中文）
   */
  quoteFontNameIfNeeded(fontName: string): string {
    if (!fontName) return fontName;

    const trimmedFont = fontName.trim();

    // 检查是否包含空格或中文字符，如果是则加上引号
    if (/\s/.test(trimmedFont) || /[\u4e00-\u9fa5]/.test(trimmedFont)) {
      return `"${trimmedFont}"`;
    }
    return trimmedFont;
  }

  get brandFonts() {
    // 从接口获取的自定义字体（品牌字体）
    return this.fontList.filter(item => item.brand === true);
  }

  get commonFonts() {
    // 通用字体（原始硬编码字体）
    return this.fontList.filter(item => item.brand !== true);
  }

  get fileExtension() {
    if (!this.uploadForm.file) return '';
    const name = this.uploadForm.file.name;
    return name.substring(name.lastIndexOf('.') + 1).toUpperCase();
  }

  get isFormValid() {
    return this.uploadForm.name && this.uploadForm.file && this.uploadForm.fallback;
  }

  @Watch('value', { immediate: true })
  onValuePropChange(val: any) {
    const resolved = this.resolveFontOptionValue(val);
    this.localValue = resolved;
    const raw = this.normalizeValue(val).trim();
    // 存库字符串与下拉 value 不一致时回写规范值，否则界面长期空白
    if (resolved && raw && resolved !== raw) {
      this.$nextTick(() => {
        this.$emit('input', resolved);
        this.$emit('change', resolved);
      });
    }
  }

  handleChange(val: string) {
    const next = this.resolveFontOptionValue(val);
    console.log('handleChange', { val, next })
    this.localValue = next;
    this.$emit('input', next);
    this.$emit('change', next);
    this.$nextTick(() => {
      const sel: any = this.$refs.fontFamilySelect;
      if (sel && typeof sel.blur === 'function') {
        sel.blur();
      }
    });
  }

  showUploadDialog() {
    (window as any)?.gtagClick?.(
      'form_edit_addBrandFont',
      '弹窗_编辑订阅工具_添加品牌字体'
    );
    this.uploadForm = {
      name: '',
      file: null,
      fallback: 'CartxHelvetica'
    };
    this.dialogVisible = true;
  }

  handleFileChange(file: any) {
    const isFont = /\.(ttf|otf|woff|woff2)$/i.test(file.name);
    if (!isFont) {
      Message.error(this.$t('components.fontpicker.text_53ea80fd4e0a4f20') as string);
      return;
    }

    // element-ui el-upload 返回的对象里通常包含 raw（原始 File）
    const raw = file?.raw || file;
    const size = typeof raw?.size === 'number' ? raw.size : 0;
    const maxSize = 1024 * 1024; // 1MB
    if (size > maxSize) {
      Message.error(this.$t('components.fontpicker.text_5b574f5365874ef6') as string);
      return;
    }

    this.uploadForm.file = raw;
    // 只在字体名称为空时，自动使用文件名（不带扩展名）
    if (!this.uploadForm.name) {
      this.uploadForm.name = file.name.substring(0, file.name.lastIndexOf('.'));
    }

    // 手动触发验证以清除可能的错误状态
    this.$nextTick(() => {
      const form: any = this.$refs.uploadForm;
      if (form && typeof form.validateField === 'function') {
        form.validateField('file');
        form.validateField('name');
      }
    });
  }

  removeFile() {
    this.uploadForm.file = null;
  }

  async submitUpload() {
    if (!this.isFormValid) return;

    this.uploading = true;
    try {
      // 第一步：上传字体文件
      const uploadResponse: any = await uploadFont(this.uploadForm.file);

      if (!uploadResponse.data.data || uploadResponse.data.data.length === 0) {
        Message.error(uploadResponse.message || this.$t('components.fontpicker.font-file-upload-failed') as string);
        return;
      }

      const uploadResult = uploadResponse.data.data[0];
      if (!uploadResult.success) {
        Message.error(uploadResult.message || this.$t('components.fontpicker.font-file-upload-failed') as string);
        return;
      }

      // 第二步：保存字体信息到数据库
      const saveResponse: any = await saveFont({
        fileName: uploadResult.fileName,
        url: uploadResult.url,
        fontName: this.uploadForm.name,
        defaultFont: this.uploadForm.fallback
      });

      if (!saveResponse.data || saveResponse.data.code !== 0) {
        Message.error(saveResponse.message || this.$t('components.fontpicker.text_5b574f534fe1606f') as string);
        return;
      }

      const savedFont = (saveResponse.data.data && saveResponse.data.data[0]) || {
        fontName: this.uploadForm.name,
        defaultFont: this.uploadForm.fallback,
        url: uploadResult.url,
      };

      // 上传成功后，清空缓存并重新拉取一次列表，保证与后端数据一致
      companyFontsCache = null;
      await this.loadCompanyFonts();
      this.lastFontListRefreshAt = Date.now();

      // 通知同页其它 FontPicker 实例刷新
      try {
        window.dispatchEvent(new CustomEvent(FONT_LIST_UPDATED_EVENT));
      } catch (e) {
        // 老环境兜底
        window.dispatchEvent(new Event(FONT_LIST_UPDATED_EVENT));
      }

      // 动态加载字体到页面（兜底）
      if (savedFont.url && savedFont.fontName) {
        loadCustomFont(savedFont.url, savedFont.fontName);
      }

      // 更新当前选中的字体（与列表项 value 一致：fontName, fallback）
      const selectedValue = `${this.quoteFontNameIfNeeded(savedFont.fontName)}, ${savedFont.defaultFont || this.uploadForm.fallback}`;
      this.localValue = selectedValue;
      this.handleChange(selectedValue);
      this.dialogVisible = false;

      // 强制更新以确保UI正确显示
      this.$nextTick(() => {
        this.$forceUpdate();
      });

      Message.success(this.$t('components.fontpicker.text_5b574f534e0a4f20') as string);

    } catch (error: any) {
      console.error('Font upload failed:', error);
      // axios 全局拦截器已在非 0 code 时展示错误提示；
      // 这里不再重复 Message.error，避免“重复字体名”出现两次提示。
    } finally {
      this.uploading = false;
    }
  }
}

export default FontPicker;
